/**
 * Google Identity Services (the web sign-in library) loader.
 *
 * We only ever use it to obtain an ID token, which the backend validates at
 * /api/auth/authenticate — exactly the same door the mobile apps use. Nothing
 * about the session lives in Google's SDK.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';

// The Firebase project's auto-created *Web* OAuth client. Committed rather than
// left to env because (a) an OAuth client id is a public identifier — it ships
// in this bundle either way — and (b) a build from a machine without a .env
// would silently drop the Google button, which is exactly what happened before.
// It is already in the backend's baked-in allowedAudiences (modauth/authpkg.go),
// so dev and prod both accept it without GOOGLE_WEB_CLIENT_IDS being set.
const DEFAULT_WEB_CLIENT_ID =
  '274576509985-kahr3ak04pht2cj8koigivbdmj186hq0.apps.googleusercontent.com';

export const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID || DEFAULT_WEB_CLIENT_ID;

let loadPromise = null;

/** Resolves with window.google once the GIS script is ready. */
export function loadGoogleIdentity() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    const script = existing || document.createElement('script');

    script.addEventListener('load', () => {
      if (window.google?.accounts?.id) resolve(window.google);
      else reject(new Error('google_identity_unavailable'));
    });
    script.addEventListener('error', () => {
      loadPromise = null;
      reject(new Error('google_script_failed'));
    });

    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loadPromise;
}

/**
 * Initialize GIS and render its button into `element`.
 *
 * `onCredential` receives the raw ID token string. GIS renders its own button
 * markup (an iframe) — Google's terms don't allow a hand-rolled substitute —
 * so the caller styles the surrounding container, not the button itself.
 */
export async function renderGoogleButton(element, { onCredential, locale = 'ar', width = 320 }) {
  if (!GOOGLE_CLIENT_ID) throw new Error('missing_client_id');

  const google = await loadGoogleIdentity();

  google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response) => {
      if (response?.credential) onCredential(response.credential);
    },
    // One Tap is deliberately off: this is an explicit "sign in" screen, and
    // an auto-prompt on a shared family computer is the wrong default.
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  google.accounts.id.renderButton(element, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    shape: 'pill',
    text: 'signin_with',
    logo_alignment: 'center',
    locale,
    width,
  });

  return google;
}

/** Clear GIS's own session hint so the next sign-in shows the account chooser. */
export function disableGoogleAutoSelect() {
  try {
    window.google?.accounts?.id?.disableAutoSelect();
  } catch {}
}
